import { useMemo } from "react";
import { groupBy } from "lodash";
import type { ComponentMeta } from "@circle-react-shared/BuilderUI/BuilderUI.types";
import type {
  BaseBlock,
  BaseProp,
} from "@circle-react-shared/BuilderUI/DataModel.types";
import {
  isChildrenProp,
  isProp,
  isPropsMetaStyles,
  isStylesProp,
} from "@circle-react-shared/BuilderUI/Helpers/typePredicates";
import type { PropsMeta } from "@circle-react-shared/BuilderUI/PropsMeta.types";
import type { StyleMetaAttributes } from "@circle-react-shared/BuilderUI/PropsMetaStyle.types";
import type { EditorFieldProps } from "./DefaultEditorFields";

export interface CategoryMetadata {
  name: string;
  nameI18n?: string;
}

export interface CategorizedField {
  categories: CategoryMetadata[] | ReadonlyArray<CategoryMetadata>;
  name: string;
  prop: BaseProp;
}

const OTHER_CATEGORY_NAME = "other";

export type TabGroups = Record<string, Record<string, EditorFieldProps[]>>;
export type TabOnlyGroups = Record<string, EditorFieldProps[]>;

const extractProps = (
  namePrefix: string,
  prop: BaseProp,
  propMeta: PropsMeta | StyleMetaAttributes,
): CategorizedField[] => {
  if (!isStylesProp(prop)) {
    return [
      {
        name: namePrefix,
        prop,
        categories: propMeta.category || [],
      },
    ];
  }

  return Object.entries(prop.value).flatMap(([key, value]) => {
    const fullPath = `${namePrefix}.${key}`;

    if (isProp(value) && isPropsMetaStyles(propMeta)) {
      const styleMetaAttributes = propMeta.attributes[key];

      if (styleMetaAttributes) {
        return extractProps(fullPath, value, styleMetaAttributes);
      }
    }

    return [];
  });
};

const categorizeProps = (
  block: BaseBlock,
  meta: ComponentMeta,
): CategorizedField[] =>
  Object.entries(block.props)
    .filter(([, prop]) => !isChildrenProp(prop))
    .flatMap(([name, prop]) => {
      const propMeta = meta.props?.[name];
      if (!propMeta) return [];
      return extractProps(name, prop, propMeta);
    });

const groupPropsByCategories = (propsArray: CategorizedField[]) => {
  const twoLevelProps = propsArray.filter(prop => prop.categories.length === 2);
  const oneLevelProps = propsArray.filter(prop => prop.categories.length === 1);
  const unCategorizedProps = propsArray.filter(
    prop => prop.categories.length === 0,
  );

  return { twoLevelProps, oneLevelProps, unCategorizedProps };
};

const getTabCategories = (
  twoLevelProps: CategorizedField[],
  oneLevelProps: CategorizedField[],
): string[] => [
  ...new Set([
    ...oneLevelProps.map(prop => prop.categories[0].name),
    ...twoLevelProps.map(prop => prop.categories[0].name),
  ]),
];

const buildNestedGroups = (
  tabCategories: string[],
  twoLevelProps: CategorizedField[],
): TabGroups => {
  const nestedGroups: TabGroups = {};

  for (const tabCategory of tabCategories) {
    const tabProps = twoLevelProps.filter(
      prop => prop.categories[0].name === tabCategory,
    );
    const groupedBySecondary = groupBy(
      tabProps,
      prop => prop.categories[1].name,
    );

    if (Object.keys(groupedBySecondary).length > 0) {
      nestedGroups[tabCategory] = groupedBySecondary;
    }
  }

  return nestedGroups;
};

const buildTabOnlyGroups = (
  tabCategories: string[],
  oneLevelProps: CategorizedField[],
): TabOnlyGroups => {
  const tabOnlyGroups: TabOnlyGroups = {};

  for (const tabCategory of tabCategories) {
    const categoryProps = oneLevelProps.filter(
      prop => prop.categories[0].name === tabCategory,
    );
    if (categoryProps.length > 0) {
      tabOnlyGroups[tabCategory] = categoryProps;
    }
  }

  return tabOnlyGroups;
};

const handleUnCategorizedProps = (
  unCategorizedProps: CategorizedField[],
  tabCategories: string[],
  tabOnlyGroups: TabOnlyGroups,
) => {
  if (unCategorizedProps.length === 0) return;

  if (tabCategories.includes(OTHER_CATEGORY_NAME)) {
    tabOnlyGroups[OTHER_CATEGORY_NAME] =
      tabOnlyGroups[OTHER_CATEGORY_NAME] || [];
    tabOnlyGroups[OTHER_CATEGORY_NAME].push(
      ...unCategorizedProps.map(({ name, prop }) => ({ name, prop })),
    );
  } else {
    tabCategories.push(OTHER_CATEGORY_NAME);
    tabOnlyGroups[OTHER_CATEGORY_NAME] = unCategorizedProps.map(
      ({ name, prop }) => ({
        name,
        prop,
      }),
    );
  }
};

export const useCategorizedBlockProps = (
  block: BaseBlock,
  meta: ComponentMeta,
) =>
  useMemo(() => {
    const propsArray = categorizeProps(block, meta);
    const { twoLevelProps, oneLevelProps, unCategorizedProps } =
      groupPropsByCategories(propsArray);
    const tabCategories = getTabCategories(twoLevelProps, oneLevelProps);

    const nestedGroups = buildNestedGroups(tabCategories, twoLevelProps);
    const tabOnlyGroups = buildTabOnlyGroups(tabCategories, oneLevelProps);

    handleUnCategorizedProps(unCategorizedProps, tabCategories, tabOnlyGroups);

    return {
      nestedGroups,
      tabOnlyGroups,
      unCategorizedProps: tabCategories.length === 0 ? unCategorizedProps : [],
      tabCategories,
    };
  }, [block, meta]);
